// Whether the Questions panel's current view can take a question. dashaiChat
// owns that rule, so the panel asks it whenever the panel is open and the
// dashboard, view, workspace or market changes, and renders the reason it
// gives. A failed check leaves the panel usable, because dashaiChat still
// refuses a question sent from a blocked view.

import { useEffect, useRef, useState } from "react";

import { getChatUrl, postWithAuthRetry } from "../lib/chatClient";
import type { DashboardPanelKey } from "./useDashboardPanelChat";

type PanelStatusKind = "checking" | "askable" | "blocked" | "unknown";

export interface PanelStatus {
  status: PanelStatusKind;
  /** The server's reason, set only when the view is blocked. */
  reason: string | null;
}

export interface PanelStatusInput {
  /** False while the panel is closed, so a closed panel sends nothing. */
  enabled: boolean;
  dashboard: DashboardPanelKey;
  view: string | null;
  userId: string;
  idClient: string;
  country: string;
}

const CHECKING: PanelStatus = { status: "checking", reason: null };

export function usePanelStatus({
  enabled,
  dashboard,
  view,
  userId,
  idClient,
  country,
}: PanelStatusInput): PanelStatus {
  const [state, setState] = useState<PanelStatus>(CHECKING);
  // Each check gets a number, so a late answer for a view the page has left is dropped.
  const latest = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const check = ++latest.current;
    const controller = new AbortController();
    setState(CHECKING);

    void (async () => {
      let next: PanelStatus = { status: "unknown", reason: null };
      try {
        const url = getChatUrl();
        if (!url) throw new Error("Chat function URL not configured");
        const res = await postWithAuthRetry(
          url,
          {
            mode: "panel_status",
            user_id: userId,
            id_client: idClient,
            country,
            dashboard: { key: dashboard, view },
          },
          controller.signal
        );
        const body = (await res.json().catch(() => null)) as {
          askable?: boolean;
          reason?: string;
        } | null;
        if (res.ok && body?.askable === true) next = { status: "askable", reason: null };
        else if (res.ok && body?.askable === false && body.reason)
          next = { status: "blocked", reason: body.reason };
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error(
          "[QuestionsPanel] status check failed",
          err instanceof Error ? err.message : err
        );
      }
      if (check === latest.current) setState(next);
    })();

    return () => controller.abort();
  }, [enabled, dashboard, view, userId, idClient, country]);

  return state;
}
